import { useTranslation } from 'react-i18next';
import type { IconType } from '~/components/ui/Icons/Icons';
import SVGIcon from '~/components/ui/Icons/SVGIcon';
import { cityText, floorText, getFlexibilityDescription, getUuidLabel } from '~/components/utils/officeData';
import type { CoreOfficeDataType } from '~/types/offices';
import DiscountCard from './DiscountCard';
import KeyInfoCard from './KeyInfoCard';
import { useLanguageParam } from '~/config/i18n';
import { findCapacity } from '../request/utils';
import PricePerSeatSection from './PricePerSeatSection';
import BestDealCard from '~/components/common/office/BestDealCard';

const flexibilityIcon: { [key: string]: IconType } = {
  fullAndPartTime: 'flexible',
  partTime: 'parttime',
  fullTime: 'fulltime',
};

const KeyInfo = ({ office, extraClasses = '' }: { office: CoreOfficeDataType; extraClasses?: string }) => {
  const { t } = useTranslation();
  const lang = useLanguageParam();

  const discountLongTermPercent = Math.round(office.discountLongTermPercent || 0);
  const discountLastMinutePercent = Math.round(office.discountLastMinutePercent || 0);

  const officeRoomItem = office.officeInfo.find((item) => item.name === 'officeRoom');
  const openSpaceItem = office.officeInfo.find((item) => item.name === 'openSpace');
  const meetingRoomItem = office.officeInfo.find((item) => item.name === 'meetingRoom');
  const phoneBoothItem = office.officeInfo.find((item) => item.name === 'privatePhoneBooth');

  return (
    <div className={extraClasses}>
      <div className="d-flex justify-content-between align-items-center">
        <h2 className="fo-display-4 -medium">
          {t(`office.officeType.${office.officeType}.label`)} {office.pro && t(`office.officeType.coworkingAddon`)} ·{' '}
          {cityText(office, lang)}
        </h2>
        <div className="d-none d-md-block text-2 -regular color-grey-500">{getUuidLabel(office.uuid, t)}</div>
      </div>
      <div className="d-flex align-items-center gap-2 my-1">
        <SVGIcon
          iconType="number-guests"
          width="20"
          height="20"
        />
        <span className="text-1 -regular">{`${office.minCapacity ? `${office.minCapacity}-` : ''}${office.capacity} ${t(
          'global.pluralization.i18next.seat',
          { count: office.capacity },
        )}`}</span>
        <span className="text-1 -semibold"> · </span>
        <SVGIcon
          iconType="surface-2"
          width="20"
          height="20"
        />
        <span className="text-1 -regular">
          {office.surface} {t('global.units.squareMeter')}
        </span>
      </div>
      <p className="mt-1">{t(`office.officeType.${office.officeType}.description`)}</p>
      <div className="row mt-4 g-3 mb-8">
        {!!officeRoomItem?.countService && (
          <KeyInfoCard
            iconType="individual-office"
            text={`${officeRoomItem.countService} ${t(
              `office.ammenities.officeRoom.${officeRoomItem.countService > 1 ? 'other' : 'one'}`,
            )}`}
          />
        )}

        {!!openSpaceItem?.countService && (
          <KeyInfoCard
            iconType="openspace"
            text={`${openSpaceItem.countService} ${t(
              `office.ammenities.openSpace.${openSpaceItem.countService > 1 ? 'other' : 'one'}`,
            )}`}
          />
        )}

        {!!meetingRoomItem?.countService && (
          <KeyInfoCard
            iconType="meetingroom"
            text={`${meetingRoomItem.countService} ${t(
              `office.ammenities.meetingRoom.${meetingRoomItem.countService > 1 ? 'other' : 'one'}`,
            )}`}
          />
        )}

        {!!phoneBoothItem?.countService && (
          <KeyInfoCard
            iconType="phonebox"
            text={`${phoneBoothItem.countService} ${t(
              `office.ammenities.privatePhoneBooth.${phoneBoothItem.countService > 1 ? 'other' : 'one'}`,
            )}`}
          />
        )}

        <KeyInfoCard
          iconType={office.typeOfAccess == 'privateOwnEntrance' ? 'private-entrance' : 'shared-entrance'}
          text={t(`office.typeOfAccess.${office.typeOfAccess}`)}
        />

        <KeyInfoCard
          iconType="wholefloor"
          text={floorText(office.floor, lang, t)}
        />
      </div>

      <PricePerSeatSection
        daysPerWeek={office.highestFlexibility}
        requiredCapacity={findCapacity(office)}
        office={office}
        extraClasses="d-md-none py-4 fo-border-top-sm border-grey-200"
      />

      <div className="pt-8 fo-border-top-sm border-grey-200">
        <BestDealCard extraClasses="d-md-none mb-8" />

        <div className="d-flex align-items-center">
          <div>
            <SVGIcon
              iconType={office.depositRequired ? 'deposit' : 'no-deposit'}
              height="32"
              width="32"
            />
          </div>

          <div className="ms-4">
            <h3>
              {office.depositRequired
                ? t('office.show.deposit.withDeposit.label')
                : t('office.show.deposit.noDeposit.label')}
            </h3>
            <p className="mt-1">
              {office.depositRequired
                ? t('office.show.deposit.withDeposit.description')
                : t('office.show.deposit.noDeposit.description')}
            </p>
          </div>
        </div>

        <div className="mt-6 d-flex align-items-center">
          <div>
            <SVGIcon
              iconType={flexibilityIcon[office.flexibilityCamelized]}
              height="32"
              width="32"
            />
          </div>
          <div className="ms-4">
            <h3>{t(`office.show.flexibility.${office.flexibilityCamelized}.title`)}</h3>
            <p className="mt-1">{getFlexibilityDescription(office.flexibilityCamelized, office.partTimeDays, t)}</p>
          </div>
        </div>
      </div>
      {office.discountLastMinutePercent && office.discountLastMinuteDays && (
        <DiscountCard
          title={t('office.show.lastMinuteDiscount.title')}
          discountPercentage={discountLastMinutePercent}
          description={t('office.show.lastMinuteDiscount.description', {
            discountPercentage: discountLastMinutePercent,
            amountOfDays: office.discountLastMinuteDays,
          })}
          extraClasses="mt-8"
        />
      )}
      {office.discountLongTermPercent && office.discountLongTermMonths && (
        <DiscountCard
          title={t('office.show.longTermDiscount.title')}
          discountPercentage={discountLongTermPercent}
          description={t('office.show.longTermDiscount.description', {
            discountPercentage: discountLongTermPercent,
            amountOfMonths: office.discountLongTermMonths,
          })}
          extraClasses="mt-8"
        />
      )}
    </div>
  );
};

export default KeyInfo;
